/**
 * Unified Campaign Save Service v2
 * 
 * Single source of truth for saving campaigns.
 * Uses creativeSaveService for clean creative data collection.
 */

import { supabase } from '@/integrations/supabase/client';
import { collectCreativesForSave, type CreativeForSave } from './creativeSaveService';
import { secureStorage } from '@/utils/secureStorage';

export interface CampaignSaveData {
  campaignId?: string;
  campaignName?: string;
  companyName: string;
  brandName: string;
  personInCharge: string;
  jobTitle: string;
  email: string;
  mobile: string;
  selectedLocations: any[];
  creativeUrls: string[];
  creativeMetadata: CreativeForSave[];
  totalDevices: number;
  durationDays: number;
  startDate: string;
  endDate: string;
  totalBudget: number;
  budgetCalculations?: any;
  termsAccepted: boolean;
  paymentType?: string;
  status?: 'pending' | 'approved' | 'rejected';
  paymentId?: string;
  userId?: string;
  creatorEmail?: string;
}

export interface CampaignSaveResult {
  success: boolean;
  campaignId?: string;
  error?: Error | null;
}

// Re-export for backward compatibility
export type { CreativeForSave as CreativeMetadata };

// ============ Helpers ============

const safeJsonParse = <T>(raw: string | null, fallback: T): T => {
  if (!raw || raw === 'null' || raw === 'undefined') return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const formatDateForDb = (date: string | Date | null | undefined): string | null => {
  if (!date) return null;
  try {
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
      if (date.includes('T')) return date.split('T')[0];
      const parsed = new Date(date);
      if (!isNaN(parsed.getTime())) return parsed.toISOString().split('T')[0];
    } else if (date instanceof Date && !isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch {}
  return null;
};

const generateUuid = (): string => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = new Uint8Array(16);
  globalThis.crypto?.getRandomValues?.(bytes) || 
    bytes.forEach((_, i, arr) => arr[i] = Math.floor(Math.random() * 256));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const isValidUuid = (id: string | null | undefined): boolean => {
  return typeof id === 'string' && 
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(id);
};

// ============ Data Gathering ============

/**
 * Gather all campaign data from localStorage
 */
export const gatherCampaignDataFromStorage = (): Partial<CampaignSaveData> => {
  const formData = safeJsonParse<Record<string, any>>(localStorage.getItem('formData'), {});
  const selectedLocations = safeJsonParse<any[]>(
    localStorage.getItem('selectedLocations'),
    safeJsonParse<any[]>(localStorage.getItem('selectedLocationsBackup'), [])
  );
  
  // Collect creatives with their scheduled days
  const creatives = collectCreativesForSave();
  const creativeUrls = creatives.map(c => c.url);
  
  const devices = Number(localStorage.getItem('devices')) || 0;
  const duration = Number(localStorage.getItem('duration')) || 7;
  
  let budgetCalculations = secureStorage.get<any>('budgetCalculations');
  if (!budgetCalculations?.baseTotal) {
    budgetCalculations = safeJsonParse<any>(localStorage.getItem('budgetCalculations'), {});
  }
  
  const campaignName = localStorage.getItem('campaignName') || `Campaign ${new Date().toLocaleDateString()}`;
  const currentCampaignId = localStorage.getItem('currentCampaignId');
  
  const startDate = formatDateForDb(
    localStorage.getItem('startDate_exact') || 
    localStorage.getItem('startDate') || 
    formData.startDate
  );
  const endDate = formatDateForDb(
    localStorage.getItem('endDate_exact') || 
    localStorage.getItem('endDate') || 
    formData.endDate
  );
  
  console.log('[gatherData] Campaign data summary:', {
    creatives: creatives.length,
    locations: selectedLocations.length,
    duration,
    budget: budgetCalculations.totalBudget || formData.totalBudget,
  });
  
  return {
    campaignId: currentCampaignId || undefined,
    campaignName,
    companyName: formData.companyName || '',
    brandName: formData.brandName || '',
    personInCharge: formData.personInCharge || '',
    jobTitle: formData.jobTitle || '',
    email: formData.email || '',
    mobile: formData.mobile || '',
    selectedLocations: Array.isArray(selectedLocations) ? selectedLocations : [],
    creativeUrls,
    creativeMetadata: creatives,
    totalDevices: devices,
    durationDays: duration,
    startDate: startDate || new Date().toISOString().split('T')[0],
    endDate: endDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    totalBudget: budgetCalculations.totalBudget || formData.totalBudget || 0,
    budgetCalculations,
    termsAccepted: true,
  };
};

// ============ Save Function ============

/**
 * Save campaign to database via edge function
 */
export const saveCampaignUnified = async (data: CampaignSaveData): Promise<CampaignSaveResult> => {
  console.log('=== saveCampaignUnified START ===');
  console.log('Input:', {
    campaignId: data.campaignId,
    locations: data.selectedLocations?.length,
    creatives: data.creativeMetadata?.length,
    email: data.email
  });
  
  try {
    const campaignId = isValidUuid(data.campaignId) ? data.campaignId! : generateUuid();
    
    // Log creative schedules being sent
    console.log('[saveCampaignUnified] Creative schedules:');
    data.creativeMetadata?.forEach((c, i) => {
      console.log(`  [${i}] ${c.creativeId}: days=[${c.scheduledDays?.join(',') || 'none'}] @ "${c.locationName}"`);
    });
    
    const payload = {
      campaignId,
      campaignName: data.campaignName || `Campaign ${new Date().toLocaleDateString()}`,
      companyName: data.companyName,
      brandName: data.brandName,
      personInCharge: data.personInCharge,
      jobTitle: data.jobTitle,
      email: data.email,
      mobile: data.mobile,
      selectedLocations: data.selectedLocations || [],
      creativeUrls: data.creativeUrls || [],
      creativeMetadata: data.creativeMetadata || [],
      totalDevices: data.totalDevices || 0,
      durationDays: data.durationDays,
      startDate: data.startDate,
      endDate: data.endDate,
      totalBudget: data.totalBudget,
      budgetInfo: data.budgetCalculations || {},
      termsAccepted: data.termsAccepted ?? true,
      paymentType: data.paymentType || 'online',
      status: data.status || 'pending',
      paymentId: data.paymentId,
    };
    
    console.log('[saveCampaignUnified] Calling edge function...');
    
    const { data: result, error } = await supabase.functions.invoke('create-campaign', {
      body: payload,
    });
    
    if (error) {
      console.error('[saveCampaignUnified] Error:', error);
      return { success: false, error: new Error(error.message) };
    }
    
    if (!result?.success) {
      console.error('[saveCampaignUnified] Failed:', result);
      return { success: false, error: new Error(result?.error || 'Unknown error') };
    }
    
    console.log('[saveCampaignUnified] Success:', result.campaignId);
    console.log('=== saveCampaignUnified END ===');
    
    return { success: true, campaignId: result.campaignId || campaignId };
    
  } catch (error) {
    console.error('[saveCampaignUnified] Exception:', error);
    return {
      success: false,
      error: error instanceof Error ? error : new Error('Unknown error'),
    };
  }
};

/**
 * Convenience function: gather data from storage and save
 */
export const saveCampaignFromStorage = async (
  overrides?: Partial<CampaignSaveData>
): Promise<CampaignSaveResult> => {
  // Small delay to ensure localStorage writes are complete
  await new Promise(r => setTimeout(r, 50));
  
  const data = gatherCampaignDataFromStorage();
  
  const finalData: CampaignSaveData = {
    campaignId: overrides?.campaignId || data.campaignId,
    campaignName: overrides?.campaignName || data.campaignName || '',
    companyName: overrides?.companyName || data.companyName || '',
    brandName: overrides?.brandName || data.brandName || '',
    personInCharge: overrides?.personInCharge || data.personInCharge || '',
    jobTitle: overrides?.jobTitle || data.jobTitle || '',
    email: overrides?.email || data.email || '',
    mobile: overrides?.mobile || data.mobile || '',
    selectedLocations: overrides?.selectedLocations || data.selectedLocations || [],
    creativeUrls: overrides?.creativeUrls || data.creativeUrls || [],
    creativeMetadata: overrides?.creativeMetadata || data.creativeMetadata || [],
    totalDevices: overrides?.totalDevices ?? data.totalDevices ?? 0,
    durationDays: overrides?.durationDays ?? data.durationDays ?? 7,
    startDate: overrides?.startDate || data.startDate || '',
    endDate: overrides?.endDate || data.endDate || '',
    totalBudget: overrides?.totalBudget ?? data.totalBudget ?? 0,
    budgetCalculations: overrides?.budgetCalculations || data.budgetCalculations,
    termsAccepted: overrides?.termsAccepted ?? data.termsAccepted ?? true,
    paymentType: overrides?.paymentType || data.paymentType,
    status: overrides?.status || data.status,
    paymentId: overrides?.paymentId,
    userId: overrides?.userId,
    creatorEmail: overrides?.creatorEmail || data.email,
  };
  
  return saveCampaignUnified(finalData);
};

// ============ Backward Compatibility Exports ============

export { collectCreativesForSave as collectAllCreativeUrls };
export { generateUuid };
